import React from 'react';
import { useAppSelector } from '../../hooks/redux';
import { selectHasPermission, selectHasRole, selectUser } from '../../store/slices/authSlice';
import { PermissionKey, RoleName } from '../../types/auth';

interface PermissionGateProps {
  children: React.ReactNode;
  permission?: PermissionKey;
  permissions?: PermissionKey[]; // Require ANY of these permissions
  requireAllPermissions?: PermissionKey[]; // Require ALL of these permissions
  role?: RoleName;
  roles?: RoleName[]; // Require ANY of these roles
  requireAllRoles?: RoleName[]; // Require ALL of these roles
  requireAdmin?: boolean;
  requireSuperAdmin?: boolean;
  fallback?: React.ReactNode;
  hideOnNoAccess?: boolean; // If true, returns null instead of fallback
}

/**
 * PermissionGate component for conditional rendering based on user permissions and roles
 * 
 * @example
 * // Basic permission check
 * <PermissionGate permission="users:create">
 *   <Button>Create User</Button>
 * </PermissionGate>
 * 
 * @example
 * // Multiple permissions (ANY)
 * <PermissionGate permissions={["users:create", "users:update"]}>
 *   <Button>Manage Users</Button>
 * </PermissionGate>
 * 
 * @example
 * // Role-based access
 * <PermissionGate requireAdmin>
 *   <AdminPanel />
 * </PermissionGate>
 * 
 * @example
 * // With fallback content
 * <PermissionGate permission="users:read" fallback={<div>Access Denied</div>}>
 *   <UsersList />
 * </PermissionGate>
 */
const PermissionGate: React.FC<PermissionGateProps> = ({
  children,
  permission,
  permissions,
  requireAllPermissions,
  role,
  roles,
  requireAllRoles,
  requireAdmin = false,
  requireSuperAdmin = false,
  fallback = null,
  hideOnNoAccess = false,
}) => {
  const user = useAppSelector(selectUser);
  
  const hasPermission = useAppSelector(state => 
    permission ? selectHasPermission(state, permission) : true
  );
  
  const hasAnyPermission = useAppSelector(state => {
    if (!permissions || permissions.length === 0) return true;
    return permissions.some(p => selectHasPermission(state, p));
  });
  
  const hasAllPermissions = useAppSelector(state => {
    if (!requireAllPermissions || requireAllPermissions.length === 0) return true;
    return requireAllPermissions.every(p => selectHasPermission(state, p));
  });
  
  const hasRole = useAppSelector(state => 
    role ? selectHasRole(state, role) : true
  );
  
  const hasAnyRole = useAppSelector(state => {
    if (!roles || roles.length === 0) return true;
    return roles.some(r => selectHasRole(state, r));
  });
  
  const hasAllRoles = useAppSelector(state => {
    if (!requireAllRoles || requireAllRoles.length === 0) return true;
    return requireAllRoles.every(r => selectHasRole(state, r));
  });
  
  const isAdmin = useAppSelector(state => 
    requireAdmin ? selectHasRole(state, 'Admin') || selectHasRole(state, 'SuperAdmin') : true
  );
  
  const isSuperAdmin = useAppSelector(state => 
    requireSuperAdmin ? selectHasRole(state, 'SuperAdmin') : true
  );

  // If user is not authenticated, deny access
  if (!user) {
    return hideOnNoAccess ? null : (fallback as React.ReactElement);
  }

  // Check all conditions
  const hasAccess = hasPermission && 
                   hasAnyPermission && 
                   hasAllPermissions && 
                   hasRole && 
                   hasAnyRole && 
                   hasAllRoles && 
                   isAdmin && 
                   isSuperAdmin;

  if (hasAccess) {
    return <>{children}</>;
  }

  return hideOnNoAccess ? null : (fallback as React.ReactElement);
};

export default PermissionGate;
